import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  FormGroup,
  Input,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Stack } from '@mui/system'
import moment from 'moment'
import React, { useEffect } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import Loader from '../../../common/loader/Loader'
import { removeTypename, toEuroString } from '../../../common/utils'
import { User } from '../../auth/types'
import { i18n } from '../../index/index'
import {
  fetchStationSalesIncentiveProducts,
  getMonthlyTargetsByBudget,
  upsertIncentiveSalesTargetMutation,
} from './incentiveProductQueries'
import { IncentiveSalesTarget, MonthlySalesIncentive, MonthlyTarget, ShiftPercentages } from './incentiveProductTypes'
import LastYearOutcomeRow from './LastYearOutcomeRow'
import MonthlyTargetsRow from './MonthlyTargetsRow'

interface IncentiveSalesMonthlyTargetsProps {
  date?: Date
  stationId: string
  incentiveSalesTargetData: IncentiveSalesTarget
  user?: User
  lastYearOutcomeData?: MonthlySalesIncentive[]
}

const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

const defaultShiftPercentages: ShiftPercentages = { Morning: 33.3, Evening: 33.3, Night: 33.3 }

interface FormData {
  yearlyTarget: number
  monthlyTargets: MonthlyTarget[]
  shiftPercentages: ShiftPercentages
}

export function IncentiveSalesTargets({
  date,
  stationId,
  incentiveSalesTargetData,
  user,
  lastYearOutcomeData,
}: IncentiveSalesMonthlyTargetsProps) {
  if (!user) {
    return <></>
  }
  const { incentiveSalesTarget, monthlyTargets } = incentiveSalesTargetData
  const [isEditing, setIsEditing] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const [fetchMonthlyTargetsByBudget, { data: monthlyTargetsByBudgetData }] = useLazyQuery<
    { monthlyTargetsByBudget: MonthlyTarget[] },
    { stationId: string; year: number; yearlyTarget: number }
  >(getMonthlyTargetsByBudget, {
    fetchPolicy: 'network-only',
  })

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isValid },
  } = useForm<FormData>({
    defaultValues: {
      yearlyTarget: incentiveSalesTarget || 0,
      monthlyTargets: monthlyTargets ?? [],
      shiftPercentages: removeTypename<ShiftPercentages>(
        incentiveSalesTargetData.shiftPercentages ?? defaultShiftPercentages,
      ),
    },
    mode: 'onChange',
  })

  const watchedYearlyTarget = useWatch({ control, name: 'yearlyTarget' })
  const watchedMonthlyTargets = useWatch({ control, name: 'monthlyTargets' })
  const canEditYearlyTarget = isEditing && user.userType === 'SokAdmin'
  const canChangeMonthlyTargets = !date || moment(date).year() > moment().year()

  const [mutationFunction, { loading }] = useMutation(upsertIncentiveSalesTargetMutation, {
    refetchQueries: [fetchStationSalesIncentiveProducts, 'FetchStationSalesIncentiveProducts'],
    fetchPolicy: 'network-only',
    onError: () => setError(i18n.t('admin.incentiveSalesTargets.updateError')),
  })

  const toggleEditing = () => {
    setIsEditing(!isEditing)
  }

  const cancelEditing = () => {
    reset({
      yearlyTarget: incentiveSalesTarget || 0,
      monthlyTargets: monthlyTargets || [],
      shiftPercentages: removeTypename<ShiftPercentages>(
        incentiveSalesTargetData.shiftPercentages ?? defaultShiftPercentages,
      ),
    })
    setIsEditing(false)
  }

  const changeMonthlyTarget = (month: number, value: number) => {
    const currentTargets = watchedMonthlyTargets || []
    const updatedTargets = currentTargets.map((mt) => {
      if (mt.month === month) {
        return {
          ...mt,
          incentiveSalesTarget: value,
        }
      }
      return mt
    })
    setValue('monthlyTargets', updatedTargets)
  }

  const onSubmit = (data: FormData) => {
    mutationFunction({
      variables: {
        stationId: stationId,
        data: {
          date: date,
          incentiveSalesTarget: data.yearlyTarget,
          shiftPercentages: data.shiftPercentages,
          monthlyTargets: data.monthlyTargets.map((mt) => {
            return { month: mt.month, year: mt.year, incentiveSalesTarget: mt.incentiveSalesTarget }
          }),
        },
      },
    })
    setIsEditing(false)
  }

  const onRequestingMonthlyTargets = () => {
    fetchMonthlyTargetsByBudget({
      variables: {
        stationId: stationId,
        year: date ? moment(date).year() : moment().year(),
        yearlyTarget: watchedYearlyTarget || 0,
      },
    })
  }

  const changeYearlyTarget = (yearlyTarget: number) => {
    setValue('yearlyTarget', yearlyTarget)
  }

  useEffect(() => {
    if (monthlyTargetsByBudgetData?.monthlyTargetsByBudget) {
      setValue('monthlyTargets', monthlyTargetsByBudgetData.monthlyTargetsByBudget)
    }
  }, [monthlyTargetsByBudgetData])

  const monthlyTargetsSum = (watchedMonthlyTargets || []).reduce((sum, mt) => sum + mt.incentiveSalesTarget, 0)
  let targetDifference = monthlyTargetsSum - (watchedYearlyTarget ?? 0)
  if (Math.abs(targetDifference) < 1) {
    targetDifference = 0
  }
  const isFormValid = targetDifference === 0 && isValid

  return (
    <>
      <Typography variant='h5' gutterBottom>
        {i18n.t('admin.incentiveSalesTargets.annualTarget') + ' ' + (date ? moment(date).year() : '')}
      </Typography>
      <Paper sx={{ padding: 1 }}>
        <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
          <Alert onClose={() => setError(null)} severity='error'>
            {error}
          </Alert>
        </Snackbar>
        <Loader loading={loading}>
          <Stack direction='row' spacing={2} alignItems='center' marginBottom={2}>
            {!canEditYearlyTarget && (
              <Typography variant='h6' gutterBottom>
                {toEuroString(watchedYearlyTarget || 0)}
              </Typography>
            )}
            <FormGroup>
              {canEditYearlyTarget && (
                <Controller
                  name='yearlyTarget'
                  control={control}
                  render={({ field }) => (
                    <Input
                      type='number'
                      value={field.value || ''}
                      onChange={(event) => changeYearlyTarget(Number(event.target.value))}
                    />
                  )}
                />
              )}
              {isEditing && canChangeMonthlyTargets && (
                <Button variant='contained' onClick={onRequestingMonthlyTargets}>
                  {i18n.t('admin.incentiveSalesTargets.divideYearlyTargetByBudget')}
                </Button>
              )}
            </FormGroup>
            <Typography variant='body1' gutterBottom color={getTargetDifferenceColor(targetDifference)}>
              ({getTargetDifferenceText(targetDifference)})
            </Typography>
          </Stack>
          <Typography gutterBottom>{i18n.t('admin.incentiveSalesTargets.monthlyTargetsHeadingText')}</Typography>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <MonthHeaderRow months={months} />
              </TableHead>
              <TableBody>
                <MonthlyTargetsRow
                  months={months}
                  monthlyTargetFormData={watchedMonthlyTargets || []}
                  isEditing={isEditing}
                  changeMonthlyTarget={changeMonthlyTarget}
                />
              </TableBody>
            </Table>
            <Typography gutterBottom>{i18n.t('admin.incentiveSalesTargets.lastYearOutcomeHeadingText')}</Typography>
            <Table size='small'>
              <TableHead>
                <MonthHeaderRow months={months} />
              </TableHead>
              <TableBody>
                <LastYearOutcomeRow months={months} lastYearOutcomeData={lastYearOutcomeData} />
              </TableBody>
            </Table>
          </TableContainer>
          <Typography gutterBottom marginTop={4}>
            {i18n.t('admin.incentiveSalesTargets.shiftWeights')}
          </Typography>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t('admin.incentiveSalesTargets.morningShiftWeight')} (%)</TableCell>
                  <TableCell>{i18n.t('admin.incentiveSalesTargets.eveningShiftWeight')} (%)</TableCell>
                  <TableCell>{i18n.t('admin.incentiveSalesTargets.nightShiftWeight')} (%)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    {isEditing ? (
                      <Controller
                        name='shiftPercentages.Morning'
                        control={control}
                        render={({ field }) => (
                          <Input
                            type='number'
                            value={field.value || ''}
                            onChange={(event) => field.onChange(Number(event.target.value))}
                          />
                        )}
                      />
                    ) : (
                      watch('shiftPercentages.Morning')
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <Controller
                        name='shiftPercentages.Evening'
                        control={control}
                        render={({ field }) => (
                          <Input
                            type='number'
                            value={field.value || ''}
                            onChange={(event) => field.onChange(Number(event.target.value))}
                          />
                        )}
                      />
                    ) : (
                      watch('shiftPercentages.Evening')
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <Controller
                        name='shiftPercentages.Night'
                        control={control}
                        render={({ field }) => (
                          <Input
                            type='number'
                            value={field.value || ''}
                            onChange={(event) => field.onChange(Number(event.target.value))}
                          />
                        )}
                      />
                    ) : (
                      watch('shiftPercentages.Night')
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction='row' spacing={2} marginTop={2} justifyContent='flex-end'>
            {isEditing && (
              <>
                <Button
                  style={{
                    marginRight: `10px`,
                  }}
                  variant='contained'
                  color='secondary'
                  onClick={() => cancelEditing()}
                >
                  {i18n.t('buttons.cancel')}
                </Button>
                <Button disabled={!isFormValid} variant='contained' color='primary' onClick={handleSubmit(onSubmit)}>
                  {i18n.t('buttons.save')}
                </Button>
              </>
            )}
            {!isEditing && (
              <Button variant='contained' color='primary' onClick={toggleEditing}>
                {i18n.t('buttons.edit')}
              </Button>
            )}
          </Stack>
        </Loader>
      </Paper>
    </>
  )
}

function getTargetDifferenceColor(difference: number): 'success' | 'error' {
  if (difference === 0) {
    return 'success'
  }
  return 'error'
}

function getTargetDifferenceText(difference: number): string {
  if (difference >= 0) {
    return `+${toEuroString(difference)}`
  } else if (difference < 0) {
  }
  return `${toEuroString(difference)}`
}

function MonthHeaderRow({ months }: { months: number[] }) {
  return (
    <TableRow>
      {months.map((month) => (
        <TableCell key={month}>
          {moment()
            .locale('fi')
            .month(month - 1)
            .format('MMM')}
        </TableCell>
      ))}
    </TableRow>
  )
}
